/*
 * Copyright Evollis 2021
 * All Rights Reserved.
 *
 * All information contained herein is, and remains the property of Evollis.
 * The intellectual and technical concepts contained herein are
 * proprietary to Evollis and are protected by trade secret and copyright law.
 * Dissemination of this information or reproduction of this material
 * is STRICTLY FORBIDDEN unless prior written permission is obtained
 * from Evollis.
 */
import { loc } from "../../../../core/utils"
import genericSchema from "../ClientPageValidator"
import { getBicNl } from "../bicNlStore"
import * as yup from "yup"

const yearRegex = /^[0-9]{4}$/
const birthDateRegex = /^[0-9]{2}\/[0-9]{2}\/[0-9]{4}$/
const emailRegex = /^[a-zA-Z0-9_\-.]+@[a-zA-Z0-9_\-.]+\.[a-zA-Z0-9_\-.]+$/
const postalCodeRegex = /^[0-9]{4}[a-zA-Z]{2}$/
const phoneRegex = /^[0-9]{11}$/

const requiredMessage = () => loc`Enter data`
const invalidMessage = () => loc`Invalid data`
const phoneNumberMessage = () => loc`Invalid phone number`
const emailNotValidMessage = () => loc`Email invalid`

let schema = yup.object().shape({
  identity: yup.object().shape({
    name: yup.string().nullable().required(requiredMessage).max(25, invalidMessage),
    firstName: yup.string().nullable().required(requiredMessage).max(25, invalidMessage),
    citizenShipCountry: yup.string().nullable().required(requiredMessage),
    birthDate: yup
      .string()
      .nullable()
      .test({
        name: "birthDateTest",
        message: "",
        test(value) {
          if (!value) {
            return this.createError({
              message: requiredMessage,
              path: "identity.birthDate"
            })
          }
          if (!value.match(birthDateRegex)) {
            return this.createError({
              message: invalidMessage,
              path: "identity.birthDate"
            })
          }
          return true
        }
      })
  }),
  coordinates: yup.object().shape({
    iban: yup
      .string()
      .nullable()
      .test({
        name: "ibanTest",
        message: "",
        test(value) {
          if (!value) {
            return this.createError({
              message: requiredMessage,
              path: "coordinates.iban"
            })
          }
          if (
            value.replace(/\s/g, "").length !== 18 ||
            value.substr(0, 2).toUpperCase() !== "NL" ||
            !getBicNl()[value.replace(/\s/g, "").substr(4, 4)]
          ) {
            return this.createError({
              message: invalidMessage,
              path: "coordinates.iban"
            })
          }
          return true
        }
      }),
    bic: yup.string().nullable().required(requiredMessage),
    yearBankSeniority: yup
      .string()
      .nullable()
      .test({
        name: "yearBankSeniorityTest",
        message: "",
        test(value) {
          if (!value) {
            return this.createError({
              message: requiredMessage,
              path: "coordinates.yearBankSeniority"
            })
          }
          if (!value.match(yearRegex)) {
            return this.createError({
              message: invalidMessage,
              path: "coordinates.yearBankSeniority"
            })
          }
          return true
        }
      }),
    postalCode: yup
      .string()
      .nullable()
      .test({
        name: "postalCodeyTest",
        message: "",
        test(value) {
          if (!value) {
            return this.createError({
              message: requiredMessage,
              path: "coordinates.postalCode"
            })
          }
          if (!value.replaceAll(" ", "").match(postalCodeRegex)) {
            return this.createError({
              message: invalidMessage,
              path: "coordinates.postalCode"
            })
          }
          return true
        }
      }),
    phoneNumber: yup
      .string()
      .nullable()
      .test({
        name: "phoneTest",
        message: "",
        test(value) {
          if (!value) {
            return this.createError({
              message: requiredMessage,
              path: "coordinates.phoneNumber"
            })
          }
          if (!value.match(phoneRegex)) {
            return this.createError({
              message: phoneNumberMessage,
              path: "coordinates.phoneNumber"
            })
          }
          return true
        }
      }),
    email: yup
      .string()
      .nullable()
      .required(requiredMessage)
      .test({
        name: "email",
        message: "",
        test(value) {
          if (!value) return true
          if (!value?.match(emailRegex)) {
            return this.createError({
              message: emailNotValidMessage,
              path: "coordinates.email"
            })
          }
          return true
        }
      })
  }),
  situation: yup.object().shape({
    profession: yup.string().nullable().required(requiredMessage),
    workingSituation: yup.string().nullable().required(requiredMessage),
    dependentChildrenNumber: yup.number().nullable().required(requiredMessage).min(0, invalidMessage()),
    employmentDate: yup
      .string()
      .nullable()
      .test({
        name: "employmentDateTest",
        message: "",
        test(value) {
          if (!value && !this.options.context.hideEmploymentInfo) {
            return this.createError({
              message: requiredMessage,
              path: "situation.employmentDate"
            })
          }
          return true
        }
      }),
    residenceDate: yup.string().nullable().required(requiredMessage)
  }),
  budget: yup.object().shape({
    netMonthlyIncome: yup.string().nullable().required(requiredMessage),
    otherIncome: yup.string().nullable().required(requiredMessage),
    rent: yup.string().nullable().required(requiredMessage),
    otherCharges: yup.string().nullable().required(requiredMessage),
    numberMonthsIncome: yup.string().nullable().required(requiredMessage)
  })
})

export default (values) => schema.concat(genericSchema).validate(values, { abortEarly: false, context: values })
